import { Dispatch } from '@reduxjs/toolkit';

import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import {
  AnalyticsQueryTemplate,
  QueryParams,
  QueryType,
  RelativeTimeSpan,
} from 'dashboard/models/analytics-query-template';
import { loadQueryTemplate as loadInquiryQueryTemplates } from 'dashboard/redux/actions/metrics-inquiry';
import {
  archiveAnalyticsQueryTemplate as archiveAnalyticsQueryTemplateAction,
  setAnalyticsQueryTemplates,
  setNewAnalyticsQueryTemplate,
} from 'dashboard/redux/slices/analytics-query-templates';
import {
  loadChecksQueryTemplate,
  loadVolumeQueryTemplate,
} from 'dashboard/redux/slices/metrics-verification';
import { ApiEndpoint, ApiModel } from 'lib/interfaces/api';
import { assertNever } from 'lib/utils/types';

interface GetAnalyticsQueryTemplatesResponse {
  data: AnalyticsQueryTemplate[];
}

export function getAnalyticsQueryTemplates() {
  return async (dispatch: Dispatch) => {
    const queryTemplateResponse =
      await SessionClientDashboard.getEndpoint<GetAnalyticsQueryTemplatesResponse>(
        ApiEndpoint.AnalyticsQueryTemplates,
      );

    dispatch(
      setAnalyticsQueryTemplates({
        templates: queryTemplateResponse.data,
      }),
    );
  };
}

export function createAnalyticsQueryTemplate({
  name,
  relativeTimeSpan,
  query,
}: {
  name: string;
  relativeTimeSpan: RelativeTimeSpan;
  query: QueryParams;
}) {
  return async (dispatch: Dispatch) => {
    const response = await SessionClientDashboard.postResourceCreate<AnalyticsQueryTemplate>({
      type: ApiModel.AnalyticsQueryTemplate,
      attributes: {
        name,
        query,
        'relative-time-span': relativeTimeSpan,
      },
    });
    // set new template
    dispatch(setNewAnalyticsQueryTemplate(response.data));

    // load template based on type
    switch (query.type) {
      case QueryType.Inquiries:
        dispatch(loadInquiryQueryTemplates(response.data));
        break;
      case QueryType.VerificationsVolume:
        dispatch(loadVolumeQueryTemplate({ queryTemplate: response.data }));
        break;
      case QueryType.VerificationsChecks:
        dispatch(loadChecksQueryTemplate({ queryTemplate: response.data }));
        break;
      default:
        assertNever(query.type);
    }
  };
}

interface ArchiveAnalyticsQueryRequest {
  id: string;
}

export function archiveAnalyticsQueryTemplate({ id }: ArchiveAnalyticsQueryRequest) {
  return async (dispatch: Dispatch) => {
    const archivedTemplate = await SessionClientDashboard.deleteResource<AnalyticsQueryTemplate>(
      ApiEndpoint.AnalyticsQueryTemplates,
      id,
    );
    dispatch(archiveAnalyticsQueryTemplateAction(archivedTemplate.data));
  };
}
